import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { XIcon, EyeSlashIcon, EyeIcon } from './Icons';

/**
 * Enhanced Input component with better design and features
 */
const Input = React.forwardRef(({ 
  className, 
  type = "text", 
  error,
  success,
  leftIcon,
  rightIcon,
  clearable = false,
  onClear,
  size = 'default', // 'sm', 'default', 'lg'
  variant = 'default', // 'default', 'filled', 'ghost'
  ...props 
}, ref) => {
  const { theme } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  
  // Size variants
  const sizeClasses = {
    sm: 'h-8 px-2.5 py-1 text-xs',
    default: 'h-10 px-3 py-2 text-sm',
    lg: 'h-12 px-4 py-3 text-base'
  };

  // Variant styles
  const getVariantClasses = () => {
    switch (variant) {
      case 'filled':
        return 'bg-gray-100 dark:bg-gray-800 border-transparent focus:bg-white dark:focus:bg-gray-900';
      case 'ghost':
        return 'bg-transparent border-transparent focus:bg-white dark:focus:bg-gray-900 focus:border-gray-300 dark:focus:border-gray-600';
      default:
        return `${theme.colors.surface} border-gray-300 dark:border-gray-600`;
    }
  };
  
  const baseClasses = `
    flex w-full rounded-lg border transition-all duration-200
    file:border-0 file:bg-transparent file:text-sm file:font-medium
    placeholder:text-gray-400 dark:placeholder:text-gray-500
    focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500
    disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50 dark:disabled:bg-gray-800
  `;
  
  const statusClasses = error 
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
    : success 
    ? 'border-green-500 focus:border-green-500 focus:ring-green-500/20'
    : '';

  const inputType = type === 'password' && showPassword ? 'text' : type;
  const hasLeftIcon = leftIcon;
  const hasRightIcon = rightIcon || clearable || type === 'password';

  const inputElement = (
    <input
      type={inputType}
      className={cn(
        baseClasses,
        getVariantClasses(),
        sizeClasses[size] || sizeClasses.default,
        statusClasses,
        hasLeftIcon && 'pl-10',
        hasRightIcon && 'pr-10',
        theme.text.primary,
        className
      )}
      ref={ref}
      {...props}
    />
  );

  // Simple input without icons
  if (!hasLeftIcon && !hasRightIcon) {
    return inputElement;
  }

  // Input with icons
  return (
    <div className="relative">
      {/* Left Icon */}
      {leftIcon && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
          {leftIcon}
        </div>
      )}

      {inputElement}

      {/* Right Icons */}
      {hasRightIcon && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {/* Clear button */}
          {clearable && props.value && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClear && onClear();
              }}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
            >
              <XIcon className="w-4 h-4" />
            </button>
          )}

          {/* Password toggle */}
          {type === 'password' && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
            >
              {showPassword ? (
                <EyeSlashIcon className="w-4 h-4" />
              ) : (
                <EyeIcon className="w-4 h-4" />
              )}
            </button>
          )}

          {/* Custom right icon */}
          {rightIcon && (
            <div className="text-gray-400 dark:text-gray-500">
              {rightIcon}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

Input.displayName = "Input";

/**
 * InputGroup - For grouping related inputs with addons
 */
const InputGroup = ({ 
  children, 
  leftAddon, 
  rightAddon, 
  className = '',
  ...props 
}) => {
  const { theme } = useTheme();

  return (
    <div className={`flex rounded-lg ${className}`} {...props}>
      {/* Left Addon */}
      {leftAddon && (
        <div className={`
          flex items-center px-3 text-sm border border-r-0 rounded-l-lg
          bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600
          ${theme.text.secondary}
        `}>
          {leftAddon}
        </div>
      )}

      {/* Input */}
      <div className="flex-1">
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child) && child.type === Input) {
            return React.cloneElement(child, {
              className: cn(
                child.props.className,
                leftAddon && 'rounded-l-none border-l-0',
                rightAddon && 'rounded-r-none border-r-0'
              )
            });
          }
          return child;
        })}
      </div>

      {/* Right Addon */}
      {rightAddon && (
        <div className={`
          flex items-center px-3 text-sm border border-l-0 rounded-r-lg
          bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600
          ${theme.text.secondary}
        `}>
          {rightAddon}
        </div>
      )}
    </div>
  );
};

export { Input, InputGroup };
